import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { getHours, getMinutes, set } from "date-fns";
import WorkoutDetailsStore from "stores/WorkoutDetailsStore";
import DatePickerComponent from "components/DatePickerComponent/DatePickerComponent";
import TimePickerComponent from "components/TimePicker";
import { SnackbarTypeValues } from "components/Snackbar";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import { convertToUTCZero } from "utils/timezones";
import { isValidDate } from "utils/date";
import styleConst from "constants/style";
import { SubmitButton, ItemWrapper, ItemsContainer } from "./styles";
import { WithTranslation, withTranslation } from "react-i18next";

type Props = {
  onSubmit: (date: Date) => void;
  inline?: boolean;
} & SnackbarContextProps &
  WithTranslation;

type Field = "date" | "time";

interface Values {
  date?: any;
  time?: any;
}

@observer
class AddInScheduleForm extends React.Component<Props> {
  @observable values: Values = {
    date: "",
    time: "",
  };

  @observable error: Values = {
    time: "",
  };

  @computed get date() {
    if (!this.values.date || !this.values.time) {
      return undefined;
    }

    const date = set(this.values.date, {
      hours: getHours(this.values.time),
      minutes: getMinutes(this.values.time),
    });

    return isValidDate(date) ? date : undefined;
  }

  @computed get submitIsDisabled() {
    return this.error.time || !this.date;
  }

  onChange = (field: Field) => (text: Date | null) => {
    this.values[field] = text;

    if (field === "time") {
      this.error.time = "";
    }
  };

  onAddWorkoutPress = async () => {
    const { onSubmit, setMessage } = this.props;
    const t: (key: string) => string = this.props.t;

    if (!this.date || this.error.time) {
      return;
    }

    const formattedDate = convertToUTCZero(this.date);

    if (formattedDate) {
      try {
        await WorkoutDetailsStore.schedule(formattedDate);
        onSubmit(this.date);
      } catch (error: any) {
        console.log("error :>> ", error);
        if (error.message === "workouts.schedule-not-allowed") {
          setMessage(
            t("messages.schedule-not-allowed"),
            SnackbarTypeValues.ALERT,
          );
        } else if (
          error.message === "Workout already scheduled for this date"
        ) {
          setMessage(t("workouts.already-scheduled"), SnackbarTypeValues.ALERT);
        } else {
          setMessage(error?.message, SnackbarTypeValues.ALERT);
        }
      }
    }
  };

  render() {
    const { inline = false } = this.props;
    const awardStar = WorkoutDetailsStore.details?.awardStar;
    const t: (key: string) => string = this.props.t;

    if (!awardStar) return null;

    return (
      <>
        <ItemsContainer inline={inline}>
          <ItemWrapper inline={inline}>
            <DatePickerComponent
              label={t("workouts.workout-date")}
              placeholder={"mm/dd/yyyy"}
              value={this.values.date}
              onChange={this.onChange("date")}
              invalidDateMessage={t("workouts.date-error")}
              required
            />
          </ItemWrapper>
          <ItemWrapper inline={inline}>
            <TimePickerComponent
              label={t("workouts.workout-time")}
              placeholder={"00:00 AM"}
              value={this.values.time}
              onChange={this.onChange("time")}
              invalidDateMessage={t("workouts.time-error")}
              error={this.error.time}
              required
            />
          </ItemWrapper>
        </ItemsContainer>
        <div>
          <SubmitButton
            text={t("actions.add-workout")}
            onClick={this.onAddWorkoutPress}
            loading={WorkoutDetailsStore.isUpdating}
            fontSize={16}
            fontFamily={styleConst.fonts.roboto}
            fontWeight={700}
            disabled={this.submitIsDisabled}
            width={240}
            height={52}
            inline={inline}
          />
        </div>
      </>
    );
  }
}

export default withGlobalSnackbar(withTranslation()(AddInScheduleForm));
